import React, { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { permissionService } from "../services/permissionService";
import { API_BASE, api } from "../utils/api";
import { PLAN_STATE_UPDATED_EVENT } from "../utils/planSync";

// ICONS
import { FiChevronLeft, FiChevronRight, FiChevronUp, FiChevronDown, FiGrid, FiCreditCard } from "react-icons/fi";

import producticon from "../assets/product.png";
import analyticicon from "../assets/analytic.png";
import seticon from "../assets/setting.png";
import logicon from "../assets/logout.png";
import permicon from "../assets/permission.png";
import usericon from "../assets/user.png";
import subscription from "../assets/subscription.png";
import invoice from "../assets/invoice.png";
import payment from "../assets/payment.png";
import nyafaz from "../assets/nyafazlogo.png";
import IBdashboard from "../assets/Ibdash.png";
import contacticon from "../assets/contact.png";
import myorders from "../assets/orders.png";
import serviceicon from "../assets/service.png";
import salonLoginIcon from "../assets/s-login.png";
import organizationicon from "../assets/web.png";
import adminDashboardicon from "../assets/ai.png";
import scanTopupicon from "../assets/scan.png";
import brandUsersicon from "../assets/crm.png";
import makeupicon from "../assets/makeup.png";

const Sidebar = ({ dashboardOnly = false, makeupOnly = false, expanded, setExpanded }) => {
  const navigate = useNavigate();
  const hoverTimer = useRef(null);
  const menuRef = useRef(null);
  const [stylistEnabled, setStylistEnabled] = useState(true);

  useEffect(() => {
    const checkPlanAccess = async () => {
      const token = localStorage.getItem("userId");
      if (!token || permissionService.isPlatformAdmin()) {
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/subscriptions/plan-state`, {
            credentials: 'include',
            method: "GET",
          headers: {
          },
        });
        const data = await response.json();
        if (response.ok) {
          setStylistEnabled(Boolean(data?.data?.stylist_access_enabled));
        }
      } catch {
        setStylistEnabled(false);
      }
    };

    checkPlanAccess();
    window.addEventListener(PLAN_STATE_UPDATED_EVENT, checkPlanAccess);

    return () => {
      window.removeEventListener(PLAN_STATE_UPDATED_EVENT, checkPlanAccess);
    };
  }, []);

  const handleLogout = async () => {
    const userId = localStorage.getItem("userId");
    try {
      if (userId) {
        await api("/logout", {
          method: "POST",
          body: JSON.stringify({ userId }),
        });
      }
    } catch {
      /* ignore */
    }
    localStorage.clear();
    navigate("/nyafaz-salon");
  };

  const scrollUp = () => {
    if (menuRef.current) {
      menuRef.current.scrollBy({ top: -100, behavior: "smooth" });
    }
  };

  const scrollDown = () => {
    if (menuRef.current) {
      menuRef.current.scrollBy({ top: 100, behavior: "smooth" });
    }
  };

  const menuItems = [
    {
      key: "overview",
      IconComponent: FiGrid,
      label: "Overview",
      to: "/nyafaz-salon/overview",
    },
    {
      key: "makeup-catalog",
      icon: makeupicon,
      label: "Makeup Catalog",
      to: "/nyafaz-salon/makeup-catalog",
      makeupOnly: true,
    },
    {
      key: "ibdashboard",
      icon: IBdashboard,
      label: "Dashboard",
      to: "/nyafaz-salon/ib-dashboard",
      userOnly: true,
    },
    {
      key: "admin-dashboard",
      icon: adminDashboardicon,
      label: "Admin Dashboard",
      to: "/nyafaz-salon/admin-dashboard",
      adminOnly: true,
    },
    {
      key: "analytics",
      icon: analyticicon,
      label: "Kiosk flow",
      to: "/nyafaz-salon/salonflow",
      permission: [14, 15],
    },
    {
      key: "servicelist",
      icon: serviceicon,
      label: "Service list",
      to: "/nyafaz-salon/servicelist",
      userOnly: true,
    },
    {
      key: "productlist",
      icon: producticon,
      label: "Product list",
      to: "/nyafaz-salon/productlist",
      permission: 19,
    },
    {
      key: "my-orders",
      icon: myorders,
      label: "My orders",
      to: "/nyafaz-salon/my-orders",
      userOnly: true,
    },
    {
      key: "plan-details",
      icon: seticon,
      label: "Plan details",
      to: "/nyafaz-salon/plan-details",
      userOnly: true,
    },
    {
      key: "stylist-management",
      icon: salonLoginIcon,
      label: "Salon Login",
      to: "/nyafaz-salon/stylists",
      userOnly: true,
      disabled: !stylistEnabled,
    },
    {
      key: "contact",
      icon: contacticon,
      label: "Contact us",
      to: "/nyafaz-salon/contact-us",
      userOnly: true,
    },
    {
      key: "whatsapp-settings",
      icon: contacticon,
      label: "WhatsApp",
      to: "/nyafaz-salon/whatsapp-settings",
      userOnly: true,
    },
            {
      key: "billing",
      IconComponent: FiCreditCard,
      label: "Billing",
      to: "/nyafaz-salon/subscription-detail",
      userOnly: true,
    },
    {
      key: "permission",
      icon: permicon,
      label: "Permission",
      to: "/nyafaz-salon/organization-permission",
      adminOnly: true,
    },
    {
      key: "brand-users",
      icon: brandUsersicon,
      label: "Brand Users",
      to: "/nyafaz-salon/brand-users",
      adminOnly: true,
    },
    {
      key: "organization",
      icon: organizationicon,
      label: "Organizations",
      to: "/nyafaz-salon/organization",
      adminOnly: true,
    },
    {
      key: "subscriptions",
      icon: subscription,
      label: "Subscriptions",
      to: "/nyafaz-salon/subscriptions",
      adminOnly: true,
    },
    {
      key: "payment-requests",
      icon: payment,
      label: "Payment Requests",
      to: "/nyafaz-salon/payment-requests",
      adminOnly: true,
    },
    {
      key: "invoices",
      icon: invoice,
      label: "Invoices",
      to: "/nyafaz-salon/invoices",
      adminOnly: true,
    },
    {
      key: "admin-scan-topup-packs",
      icon: scanTopupicon,
      label: "Scan Top-up Pricing",
      to: "/nyafaz-salon/admin-scan-topup-packs",
      adminOnly: true,
    },
    {
      key: "admin-plan-salon-login-limits",
      icon: scanTopupicon,
      label: "Salon Login Limits",
      to: "/nyafaz-salon/admin-plan-salon-login-limits",
      adminOnly: true,
    },
    {
      key: "admin-makeup-colors",
      icon: makeupicon,
      label: "Default Colors",
      to: "/nyafaz-salon/admin-makeup-colors",
      adminOnly: true,
      section: "Makeup Defaults",
    },
    {
      key: "admin-makeup-finishes",
      icon: makeupicon,
      label: "Default Finishes",
      to: "/nyafaz-salon/admin-makeup-finishes",
      adminOnly: true,
      section: "Makeup Defaults",
    },
    {
      key: "admin-makeup-templates",
      icon: makeupicon,
      label: "Default Templates",
      to: "/nyafaz-salon/admin-makeup-templates",
      adminOnly: true,
      section: "Makeup Defaults",
    },
    {
      key: "admin-contact-messages",
      icon: contacticon,
      label: "Contact Messages",
      to: "/nyafaz-salon/admin-contact-messages",
      adminOnly: true,
    },
    {
      key: "admin-whatsapp-credentials",
      icon: contacticon,
      label: "WhatsApp",
      to: "/nyafaz-salon/admin-whatsapp-credentials",
      adminOnly: true,
    },
  ];

  const filteredMenuItems = menuItems.filter((item) => {
    if (item.disabled) return false;
    if (makeupOnly) {
      return ["overview", "makeup-catalog"].includes(item.key);
    }

    if (dashboardOnly) {
      return ["overview"].includes(item.key);
    }

    if (item.makeupOnly) return false;

    if (item.adminOnly && !permissionService.isPlatformAdmin()) return false;
    if (item.userOnly && !permissionService.showTenantUserMenus()) return false;

    if (!item.permission) return true;

    if (Array.isArray(item.permission)) {
      return permissionService.hasAny(item.permission);
    }

    return permissionService.has(item.permission);
  });

  const isAdmin = permissionService.isPlatformAdmin();
  const showMenuIcons = !isAdmin;
  const [viewport, setViewport] = useState("desktop");

  useEffect(() => {
    const updateViewport = () => {
      const width = window.innerWidth;
      if (width < 768) setViewport("mobile");
      else if (width < 1280) setViewport("tablet");
      else setViewport("desktop");
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  const effectiveExpanded = viewport === "desktop" ? (isAdmin || expanded) : expanded;
  const showExpandToggle = !isAdmin || viewport === "tablet";

  return (
    <aside
      className={`fixed left-6 top-4 h-[calc(100vh-2rem)]
      flex flex-col items-center
      transition-all duration-500 ease-in-out
      sidebar-shell ${effectiveExpanded ? "sidebar-shell--expanded" : "sidebar-shell--collapsed"}
      rounded-2xl
      z-50
      before:content-[''] before:absolute before:inset-0 before:rounded-2xl before:pointer-events-none before:border-[1px] before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_28px),radial-gradient(circle_at_bottom_left,transparent_16px,black_28px)] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in]
    `}
    >
      {!showExpandToggle ? null : (
        <button
          onClick={() => setExpanded(!expanded)}
          className="sidebar-icon-btn absolute -right-3 top-6
                    w-6 h-6 rounded-full 
                    flex items-center justify-center
                    transition shadow-[0_4px_12px_rgba(0,0,0,0.12)]"
        >
          {expanded ? <FiChevronLeft size={14} /> : <FiChevronRight size={14} />}
        </button>
      )}

      {/* LOGO */}
      <div className="flex flex-col items-center pt-4 select-none w-full">
        <img
          src={nyafaz}
          alt="Nyafaz Logo"
          className={`transition-all duration-300 object-contain
          ${effectiveExpanded ? "w-24 h-auto" : "w-8 h-auto"}`}
        />
        <div className="sidebar-divider w-6 h-px mt-3"></div>
      </div>

      {/* SCROLL UP ARROW */}
      <div className="w-full flex justify-center pt-2">
        <button
          onClick={scrollUp}
          className="sidebar-text-faint transition-colors p-1"
          title="Scroll Up"
        >
          <FiChevronUp size={20} />
        </button>
      </div>

      {/* MENU */}
      <div 
        ref={menuRef}
        className="mt-2 flex-1 flex flex-col gap-3 w-full overflow-y-auto"
      >
        {filteredMenuItems.map((item, index) => {
          const previousSection = filteredMenuItems[index - 1]?.section;
          const showSectionLabel = effectiveExpanded && item.section && item.section !== previousSection;

          return (
            <Fragment key={item.key}>
              {showSectionLabel ? (
                <div className="sidebar-text-muted px-5 pt-2 text-[11px] font-semibold uppercase tracking-[0.24em]">
                  {item.section}
                </div>
              ) : null}
              <div className="w-full flex justify-center">
                <NavLink
                  to={item.disabled ? "#" : item.to}
                  onClick={(e) => {
                    if (item.disabled) e.preventDefault();
                  }}
                  className={({ isActive }) =>
                    `sidebar-nav-link flex items-center duration-300 group
                    ${item.disabled ? "sidebar-nav-link--disabled opacity-45 cursor-not-allowed pointer-events-none" : ""}
                    ${isActive ? "sidebar-nav-link--active" : ""}
                    ${effectiveExpanded
                      ? "w-[94%] ml-auto rounded-l-xl rounded-r-none px-4 py-2 justify-start mb-0.5"
                      : "w-10 h-10 rounded-xl justify-center"
                    }`
                  }
                  title={item.disabled ? "Temporarily disabled" : item.label}
                >
                  {showMenuIcons ? (
                    <div className={`flex items-center justify-center transition-all duration-300
                      ${effectiveExpanded ? "w-7 h-7" : "w-8 h-8"}`}>
                      {item.IconComponent ? (
                        <item.IconComponent
                          className={`sidebar-menu-icon shrink-0
                            ${item.key === "stylist-management"
                              ? effectiveExpanded ? "w-6 h-6" : "w-7 h-7"
                              : effectiveExpanded ? "w-5 h-5" : "w-6 h-6"}`}
                          aria-hidden="true"
                        />
                      ) : (
                        <img
                          src={item.icon}
                          alt={item.label}
                          className={`sidebar-menu-icon-img object-contain
                            ${item.key === "stylist-management"
                              ? effectiveExpanded ? "w-6 h-6" : "w-7 h-7"
                              : effectiveExpanded ? "w-5 h-5" : "w-6 h-6"}`}
                        />
                      )}
                    </div>
                  ) : null}

                  {effectiveExpanded && (
                    <span className={`sidebar-text text-sm font-medium whitespace-nowrap opacity-100 transition-opacity duration-300 ${showMenuIcons ? "ml-4" : ""}`}>
                      {item.label}
                    </span>
                  )}
                </NavLink>
              </div>
            </Fragment>
          );
        })}
      </div>

      {/* SCROLL DOWN ARROW */}
      <div className="w-full flex justify-center pb-2">
        <button
          onClick={scrollDown}
          className="sidebar-text-faint transition-colors p-1"
          title="Scroll Down"
        >
          <FiChevronDown size={20} />
        </button>
      </div>

      <div className="w-full flex justify-center px-3 pb-2">
        <div
          className={`sidebar-text-muted text-[11px] font-medium tracking-wide transition-all ${effectiveExpanded ? "w-[94%] ml-auto px-4 text-left" : "text-center"}`}
        >
          {effectiveExpanded ? "Version 1.0.0" : "v1.0"}
        </div>
      </div>

      <div className="py-3 w-full flex justify-center">
        <button
          onClick={handleLogout}
          className={`sidebar-logout-btn sidebar-text flex items-center transition-all group
          ${effectiveExpanded
              ? "w-[94%] ml-auto rounded-l-xl rounded-r-none px-4 py-2 justify-start"
              : "w-10 h-10 rounded-xl justify-center"}`}
        >
          {showMenuIcons ? (
            <div className={`flex items-center justify-center transition-all
              ${effectiveExpanded ? "w-7 h-7" : "w-8 h-8"}`}>
              <img
                src={logicon}
                alt="Logout"
                className={`sidebar-menu-icon-img object-contain
                  ${effectiveExpanded ? "w-4.5 h-4.5" : "w-5.5 h-5.5"}`}
              />
            </div>
          ) : null}

          {effectiveExpanded && (
            <span className={`text-sm font-medium opacity-100 whitespace-nowrap ${showMenuIcons ? "ml-4" : ""}`}>Logout</span>
          )}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
